import { listBillingDocuments } from "@/core/billing/billing-documents/billing-documents-store";
import type { BillingDocument } from "@/core/billing/billing-documents/types";
import { BILLING_SOURCE_LABELS, REVENUE_TYPE_CATALOG } from "@/core/billing/billing-source-labels";
import { PAYER_TYPE_LABELS } from "@/core/billing/billing-documents/types";
import { listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import type { ValidatedInvoice } from "@/core/pharmacy/types";
import {
  listTreasuryMovements,
  totalTreasuryBalance,
} from "@/core/treasury/treasury-store";
import type { TreasuryMovement } from "@/core/treasury/types";
import {
  formatDayLabel,
  MONTH_LABELS,
  startOfDay,
  type BreakdownPoint,
  type DayFlowPoint,
  type RevenueDayPoint,
  type RevenueMonthPoint,
} from "./analytics-utils";
import type { FinancialPeriodFilter } from "./financial-period";
import { defaultFinancialPeriodFilter, resolveFinancialPeriodRange } from "./financial-period";

export interface FinancialDashboardAnalytics {
  periodLabel: string;
  trendGranularity: "day" | "month";
  revenuePeriod: number;
  inflowsPeriod: number;
  outflowsPeriod: number;
  treasuryBalance: number;
  outstandingAmount: number;
  paidPeriod: number;
  issuedPeriod: number;
  collectionRatePct: number | null;
  revenueDailyTrend: RevenueDayPoint[];
  revenueMonthlyTrend: RevenueMonthPoint[];
  treasuryDailyTrend: DayFlowPoint[];
  treasuryMonthlyTrend: DayFlowPoint[];
  revenueBySource: BreakdownPoint[];
  revenueByService: BreakdownPoint[];
  revenueByPayer: BreakdownPoint[];
  availableYears: number[];
}

const SOURCE_LABELS = BILLING_SOURCE_LABELS;

interface RevenueEvent {
  amount: number;
  at: Date;
  sourceLabel: string;
  serviceLabel: string;
  payerLabel: string;
}

function resolveBillingServiceLabel(doc: BillingDocument): string {
  if (doc.serviceLabel.trim()) return doc.serviceLabel.trim();
  return SOURCE_LABELS[doc.source];
}

function billingRevenueEvents(docs: BillingDocument[]): RevenueEvent[] {
  const events: RevenueEvent[] = [];
  for (const doc of docs) {
    if (doc.status !== "paid" || !doc.payment) continue;
    events.push({
      amount: doc.payment.amount > 0 ? doc.payment.amount : doc.subtotal,
      at: new Date(doc.payment.paidAt),
      sourceLabel: SOURCE_LABELS[doc.source],
      serviceLabel: resolveBillingServiceLabel(doc),
      payerLabel: PAYER_TYPE_LABELS[doc.payerType],
    });
  }
  return events;
}

function pharmacyRevenueEvents(invoices: ValidatedInvoice[]): RevenueEvent[] {
  return invoices.map((inv) => ({
    amount: inv.subtotal,
    at: new Date(inv.validatedAt),
    sourceLabel: "Vente comptoir",
    serviceLabel: "Pharmacie",
    payerLabel: PAYER_TYPE_LABELS.patient,
  }));
}

function aggregateBreakdown(events: RevenueEvent[], key: keyof RevenueEvent): BreakdownPoint[] {
  const map = new Map<string, number>();
  for (const ev of events) {
    const label = String(ev[key]);
    map.set(label, (map.get(label) ?? 0) + ev.amount);
  }
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
}

/** Tous les types de revenus — y compris à 0 sur la période. */
function aggregateRevenueBySource(events: RevenueEvent[]): BreakdownPoint[] {
  const totals = new Map<string, number>(
    REVENUE_TYPE_CATALOG.map((name) => [name, 0]),
  );
  for (const ev of events) {
    totals.set(ev.sourceLabel, (totals.get(ev.sourceLabel) ?? 0) + ev.amount);
  }
  return REVENUE_TYPE_CATALOG.map((name) => ({
    name,
    value: totals.get(name) ?? 0,
  }));
}

function sumInRange(events: RevenueEvent[], start: Date, end: Date): number {
  return events
    .filter((ev) => ev.at >= start && ev.at < end)
    .reduce((s, ev) => s + ev.amount, 0);
}

function eventsInRange(events: RevenueEvent[], start: Date, end: Date): RevenueEvent[] {
  return events.filter((ev) => ev.at >= start && ev.at < end);
}

function sumMovements(
  movements: TreasuryMovement[],
  type: "credit" | "debit",
  start: Date,
  end: Date,
): number {
  return movements
    .filter((m) => {
      if (m.type !== type) return false;
      const at = new Date(m.createdAt);
      return at >= start && at < end;
    })
    .reduce((s, m) => s + m.amount, 0);
}

function buildDailyRevenueTrend(
  events: RevenueEvent[],
  start: Date,
  end: Date,
): RevenueDayPoint[] {
  const points: RevenueDayPoint[] = [];
  const cursor = startOfDay(new Date(start));
  while (cursor < end) {
    const next = new Date(cursor);
    next.setDate(next.getDate() + 1);
    const dayEvents = eventsInRange(events, cursor, next);
    points.push({
      label: formatDayLabel(cursor),
      date: cursor.toDateString(),
      revenue: dayEvents.reduce((s, ev) => s + ev.amount, 0),
      documents: dayEvents.length,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return points;
}

function buildMonthlyRevenueTrend(
  events: RevenueEvent[],
  start: Date,
  end: Date,
): RevenueMonthPoint[] {
  const points: RevenueMonthPoint[] = [];
  const cursor = startOfDay(new Date(start.getFullYear(), start.getMonth(), 1));
  while (cursor < end) {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const sliceEnd = monthEnd < end ? monthEnd : end;
    const m = cursor.getMonth();
    const y = cursor.getFullYear();
    points.push({
      label: `${MONTH_LABELS[m]} ${String(y).slice(-2)}`,
      month: `${y}-${m + 1}`,
      revenue: sumInRange(events, cursor, sliceEnd),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return points;
}

function buildDailyTreasuryTrend(
  movements: TreasuryMovement[],
  start: Date,
  end: Date,
): DayFlowPoint[] {
  const points: DayFlowPoint[] = [];
  const cursor = startOfDay(new Date(start));
  while (cursor < end) {
    const next = new Date(cursor);
    next.setDate(next.getDate() + 1);
    points.push({
      label: formatDayLabel(cursor),
      date: cursor.toDateString(),
      inflows: sumMovements(movements, "credit", cursor, next),
      outflows: sumMovements(movements, "debit", cursor, next),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return points;
}

function buildMonthlyTreasuryTrend(
  movements: TreasuryMovement[],
  start: Date,
  end: Date,
): DayFlowPoint[] {
  const points: DayFlowPoint[] = [];
  const cursor = startOfDay(new Date(start.getFullYear(), start.getMonth(), 1));
  while (cursor < end) {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const sliceEnd = monthEnd < end ? monthEnd : end;
    const m = cursor.getMonth();
    const y = cursor.getFullYear();
    points.push({
      label: `${MONTH_LABELS[m]} ${String(y).slice(-2)}`,
      date: `${y}-${m + 1}`,
      inflows: sumMovements(movements, "credit", cursor, sliceEnd),
      outflows: sumMovements(movements, "debit", cursor, sliceEnd),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return points;
}

function collectYears(events: RevenueEvent[], docs: BillingDocument[]): number[] {
  const years = new Set<number>();
  for (const ev of events) years.add(ev.at.getFullYear());
  for (const doc of docs) {
    years.add(new Date(doc.createdAt).getFullYear());
    if (doc.issuedAt) years.add(new Date(doc.issuedAt).getFullYear());
    if (doc.payment?.paidAt) years.add(new Date(doc.payment.paidAt).getFullYear());
  }
  return [...years];
}

export function buildFinancialDashboardAnalytics(input?: {
  billingDocuments?: BillingDocument[];
  invoices?: ValidatedInvoice[];
  movements?: TreasuryMovement[];
  period?: FinancialPeriodFilter;
}): FinancialDashboardAnalytics {
  const docs = input?.billingDocuments ?? listBillingDocuments();
  const invoices = input?.invoices ?? listValidatedInvoices();
  const movements = input?.movements ?? listTreasuryMovements();

  const revenueEvents = [
    ...billingRevenueEvents(docs),
    ...pharmacyRevenueEvents(invoices),
  ];

  const range = resolveFinancialPeriodRange(
    input?.period ?? defaultFinancialPeriodFilter(),
  );
  const periodEvents = eventsInRange(revenueEvents, range.start, range.end);

  const issuedPeriod = docs
    .filter((d) => {
      if (d.status === "cancelled" || d.status === "pending") return false;
      const at = new Date(d.issuedAt ?? d.createdAt);
      return at >= range.start && at < range.end;
    })
    .reduce((s, d) => s + d.subtotal, 0);

  const paidPeriod = docs
    .filter((d) => {
      if (d.status !== "paid" || !d.payment) return false;
      const at = new Date(d.payment.paidAt);
      return at >= range.start && at < range.end;
    })
    .reduce((s, d) => s + (d.payment!.amount > 0 ? d.payment!.amount : d.subtotal), 0);

  const collectionRatePct =
    issuedPeriod > 0 ? Math.round((paidPeriod / issuedPeriod) * 100) : null;

  const revenueDailyTrend = buildDailyRevenueTrend(revenueEvents, range.start, range.end);
  const revenueMonthlyTrend = buildMonthlyRevenueTrend(revenueEvents, range.start, range.end);
  const treasuryDailyTrend = buildDailyTreasuryTrend(movements, range.start, range.end);
  const treasuryMonthlyTrend = buildMonthlyTreasuryTrend(movements, range.start, range.end);

  return {
    periodLabel: range.label,
    trendGranularity: range.trendGranularity,
    revenuePeriod: sumInRange(revenueEvents, range.start, range.end),
    inflowsPeriod: sumMovements(movements, "credit", range.start, range.end),
    outflowsPeriod: sumMovements(movements, "debit", range.start, range.end),
    treasuryBalance: totalTreasuryBalance(),
    outstandingAmount: docs
      .filter((d) => ["issued", "at_cashier", "proforma"].includes(d.status))
      .reduce((s, d) => s + d.subtotal, 0),
    paidPeriod,
    issuedPeriod,
    collectionRatePct,
    revenueDailyTrend,
    revenueMonthlyTrend,
    treasuryDailyTrend,
    treasuryMonthlyTrend,
    revenueBySource: aggregateRevenueBySource(periodEvents),
    revenueByService: aggregateBreakdown(periodEvents, "serviceLabel"),
    revenueByPayer: aggregateBreakdown(periodEvents, "payerLabel"),
    availableYears: collectYears(revenueEvents, docs),
  };
}
